import type { Guide } from "@/lib/types";

export const guides: Guide[] = [
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "guide-svensk-licens",
    slug_sv: "svensk-spellicens",
    slug_en: "swedish-gambling-license",
    category: "license",
    title_sv: "Svensk spellicens — så fungerar den 2026",
    title_en: "The Swedish gambling license — how it works in 2026",
    excerpt_sv:
      "Allt om hur svensk spellicens fungerar, vad Spelinspektionen kräver och vad det innebär för dig som spelare.",
    excerpt_en:
      "Everything about the Swedish gambling license: what Spelinspektionen requires and what it means for you as a player.",
    intro_sv:
      "Sedan den 1 januari 2019 har Sverige en omreglerad spelmarknad. Alla bolag som vill rikta spel mot svenska konsumenter måste ha en licens från Spelinspektionen. Licensen är inte ett kvalitetsbetyg utan ett kontrakt: operatören accepterar att följa svensk lag, ansluta sig till Spelpaus.se och hålla en hög nivå av konsumentskydd.",
    intro_en:
      "Since 1 January 2019 Sweden has had a re-regulated gambling market. Any operator that wants to target Swedish consumers must hold a license from Spelinspektionen. The license is not a quality stamp — it is a contract: the operator agrees to follow Swedish law, join Spelpaus.se and maintain a high standard of consumer protection.",
    sections: [
      {
        heading_sv: "Vem utfärdar licensen?",
        heading_en: "Who issues the license?",
        paragraphs_sv: [
          "Spelinspektionen är den svenska tillsynsmyndigheten för spel och sitter i Strängnäs. Myndigheten beviljar licens, granskar pågående verksamhet och kan utfärda varningar, sanktionsavgifter eller dra in licens om reglerna inte följs.",
          "Det finns flera typer av licenser: kommersiellt onlinespel (casino, poker, bingo), vadhållning (sport och esport), spel för allmännyttiga ändamål, samt landbaserade kasinon. Den vanligaste licensen för casinosajter du hittar online är ”kommersiellt onlinespel”.",
        ],
        paragraphs_en: [
          "Spelinspektionen is the Swedish gambling regulator, headquartered in Strängnäs. It grants licenses, supervises ongoing operations and can issue warnings, sanction fees or revoke a license if rules are broken.",
          "Several license types exist: commercial online gambling (casino, poker, bingo), betting (sports and esports), gambling for the public benefit, and land-based casinos. The most common license behind a Swedish-facing online casino is the commercial online gambling license.",
        ],
      },
      {
        heading_sv: "Vad kräver licensen i praktiken?",
        heading_en: "What does the license require in practice?",
        paragraphs_sv: [
          "En svensk licens innebär ett antal hårda krav på operatören. Vissa märks direkt när du registrerar dig — du måste till exempel logga in med BankID och sätta en insättningsgräns innan du kan spela. Andra krav är mer osynliga, men minst lika viktiga: serverdata måste lagras säkert, vinster måste betalas ut, och allt spel måste spåras för att förhindra penningtvätt.",
        ],
        paragraphs_en: [
          "A Swedish license places several hard requirements on the operator. Some are immediately visible — you must log in with BankID and set a deposit limit before you can play. Others are less visible but just as important: server data must be securely stored, winnings must be paid, and all gambling must be tracked to prevent money laundering.",
        ],
        list_sv: [
          "Anslutning till spelpaus.se — en spelare som stängt av sig själv där kan inte logga in eller skapa konto.",
          "Obligatorisk identifiering med BankID — kontot är personligt och knutet till personnumret.",
          "En bonus per spelare. Du får ett välkomsterbjudande, sedan slutar bonusarna.",
          "Insättningsgräns, förlustgräns och tidsgräns måste vara aktiva och kunna ändras dygnet runt.",
          "Tydlig redovisning av spelhistorik, transaktioner och eventuella bonusvillkor.",
          "18-årsgräns och självtest för spelproblem måste finnas synligt.",
        ],
        list_en: [
          "Mandatory connection to spelpaus.se — a self-excluded player cannot log in or open an account.",
          "Mandatory ID via BankID — the account is personal and tied to the Swedish national ID.",
          "One bonus per player. After the welcome offer, no further bonuses.",
          "Deposit, loss and time limits must be active and changeable around the clock.",
          "Transparent display of game history, transactions and any bonus terms.",
          "18+ age limit and a problem-gambling self-test must be visible.",
        ],
      },
      {
        heading_sv: "Skatter och vinster",
        heading_en: "Taxes and winnings",
        paragraphs_sv: [
          "Vinster från svenska licensierade spelbolag är skattefria för dig som privatperson. Det är operatören som betalar 22 procent skatt på sin spelvinst (bruttovinst) i Sverige. Du behöver alltså inte deklarera en vinst som är spelad hos ett svenskt licensierat bolag.",
          "Det här är en nyckelskillnad mot casinon utan svensk licens. Vinster från sajter utanför EES-området kan vara skattepliktiga. Eftersom skattereglerna kan ändras och beror på din situation rekommenderar vi att du dubbelkollar med Skatteverket eller en oberoende rådgivare.",
        ],
        paragraphs_en: [
          "Winnings from Swedish-licensed operators are tax-free for you as a private individual. The operator pays a 22 percent gambling tax on its gross gaming revenue. You do not need to declare winnings from a Swedish-licensed site.",
          "This is a key difference from casinos without a Swedish license. Winnings from sites outside the EEA can be taxable. Because tax rules change and depend on your situation, we recommend you double-check with Skatteverket or an independent advisor.",
        ],
      },
      {
        heading_sv: "Hur kontrollerar du att en sajt har svensk licens?",
        heading_en: "How do you verify that a site has a Swedish license?",
        paragraphs_sv: [
          "Tre snabba steg: leta efter Spelinspektionens svanmärke i sidfoten, kontrollera att du faktiskt blir ombedd att logga in med BankID, och slå upp operatören i Spelinspektionens publika licenslista. Om något av de tre saknas — till exempel om sajten erbjuder dig en stor bonus utan registrering — är den sannolikt inte licensierad i Sverige.",
        ],
        paragraphs_en: [
          "Three quick checks: look for Spelinspektionen's swan mark in the footer, confirm that BankID is required to register, and look up the operator in Spelinspektionen's public license list. If any of those three is missing — for example, if the site offers you a large bonus without registration — it is most likely not licensed in Sweden.",
        ],
      },
    ],
    takeaways_sv: [
      "Licens från Spelinspektionen krävs för att rikta spel mot Sverige.",
      "BankID, Spelpaus och insättningsgränser är obligatoriska.",
      "Vinster på svenskt licensierat spel är skattefria.",
      "Endast en bonus per spelare — bonusinflationen är reglerad.",
    ],
    takeaways_en: [
      "A license from Spelinspektionen is required to target Swedish players.",
      "BankID, Spelpaus and deposit limits are mandatory.",
      "Winnings on Swedish-licensed gambling are tax-free for the player.",
      "One bonus per player — the bonus race is regulated.",
    ],
    faq: [
      {
        question_sv: "Vad händer om en operatör bryter mot licensvillkoren?",
        question_en: "What happens if an operator breaks the license terms?",
        answer_sv:
          "Spelinspektionen kan utfärda en varning, ta ut en sanktionsavgift, eller i värsta fall återkalla licensen. Listan över varningar är offentlig på Spelinspektionens webbplats.",
        answer_en:
          "Spelinspektionen can issue a warning, charge a sanction fee, or in the worst case revoke the license. The list of warnings is public on Spelinspektionen's website.",
      },
      {
        question_sv: "Är Spelpaus alltid kopplat till svensk licens?",
        question_en: "Is Spelpaus always connected to a Swedish license?",
        answer_sv:
          "Ja. Alla licensierade svenska operatörer måste vara anslutna till Spelpaus.se. Casinon utan svensk licens omfattas inte och kan inte erbjuda samma skydd.",
        answer_en:
          "Yes. Every licensed Swedish operator must be connected to Spelpaus.se. Casinos without a Swedish license are not part of it and cannot offer the same protection.",
      },
      {
        question_sv: "Kan jag som svensk spela på utländska casinon?",
        question_en: "As a Swede, can I play at foreign casinos?",
        answer_sv:
          "Det finns inget förbud för dig som privatperson att spela på casinon utomlands, men casinot får inte rikta sin marknadsföring mot Sverige. Konsumentskyddet och skatten skiljer sig.",
        answer_en:
          "There is no ban on you as a private individual playing at foreign casinos, but the casino is not allowed to market to Sweden. Consumer protection and taxation differ.",
      },
    ],
    updated_at: "2026-04-30",
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "guide-spelpaus",
    slug_sv: "spelpaus",
    slug_en: "spelpaus",
    category: "responsible",
    title_sv: "Spelpaus.se — så stänger du av dig från spel",
    title_en: "Spelpaus.se — how to self-exclude from gambling",
    excerpt_sv:
      "Spelpaus är det nationella avstängningsregistret för svenska licensierade spelbolag. Här är allt du behöver veta.",
    excerpt_en:
      "Spelpaus is the Swedish national self-exclusion register for licensed operators. Here is everything you need to know.",
    intro_sv:
      "Spelpaus.se är ett verktyg som låter dig stänga av dig själv från allt licensierat spel i Sverige med ett enda BankID-klick. Tjänsten drivs av Spelinspektionen och lanserades samtidigt som den nya spellagstiftningen 2019. Avstängningen gäller alla operatörer med svensk licens — du behöver alltså inte stänga av dig på varje sajt separat.",
    intro_en:
      "Spelpaus.se is a tool that lets you self-exclude from all licensed gambling in Sweden with a single BankID click. The service is run by Spelinspektionen and launched alongside the new gambling law in 2019. The block applies to every operator with a Swedish license — you do not need to self-exclude on every site separately.",
    sections: [
      {
        heading_sv: "Hur registrerar du en avstängning?",
        heading_en: "How do you register a block?",
        paragraphs_sv: [
          "Gå till spelpaus.se, klicka på ”Stäng av dig” och logga in med BankID. Du väljer en period på 1, 3, 6 eller 12 månader. Avstängningen träder i kraft direkt och syns hos alla licensierade bolag inom några sekunder.",
          "När avstängningen är aktiv kan du inte logga in på licensierade casinosajter, du kan inte ta emot kampanjer eller direktreklam, och nya konton blockeras automatiskt vid registrering. Befintliga insättningar och vinster kan du fortfarande ta ut.",
        ],
        paragraphs_en: [
          "Go to spelpaus.se, click ”Block yourself”, and log in with BankID. Pick a period of 1, 3, 6 or 12 months. The block takes effect immediately and appears with every licensed operator within seconds.",
          "While active, you cannot log in to licensed casino sites, you cannot receive campaigns or direct marketing, and new account registrations are automatically blocked. You can still withdraw existing deposits and winnings.",
        ],
      },
      {
        heading_sv: "Vad gäller inte Spelpaus för?",
        heading_en: "What does Spelpaus not cover?",
        paragraphs_sv: [
          "Spelpaus gäller bara casinon med svensk licens. Det innebär att internationella sajter — så kallade ”casino utan svensk licens” — inte är skyldiga att kontrollera registret. Om du har spelproblem och behöver fullt skydd är det därför viktigt att inte söka upp utländska sajter under avstängningen.",
          "Spelpaus täcker inte heller landbaserade kasinon utanför Sverige, lotter köpta i livsmedelsbutik som inte kräver licens, eller vissa ideella lotterier. Det är ändå ett mycket täckande skydd för svensk online-marknad.",
        ],
        paragraphs_en: [
          "Spelpaus only covers casinos with a Swedish license. International sites — so-called ”casinos without Swedish license” — are not obligated to check the register. If you struggle with gambling and need full protection, it is therefore important not to seek out foreign sites during your block.",
          "Spelpaus also does not cover land-based casinos outside Sweden, supermarket lottery products that do not require a license, or some non-profit lotteries. It still gives very broad protection for the Swedish online market.",
        ],
      },
      {
        heading_sv: "Behöver du mer stöd?",
        heading_en: "Need more support?",
        paragraphs_sv: [
          "Spelpaus är ett verktyg, inte en behandling. Om du eller någon i din närhet kämpar med spelandet finns gratis och anonym hjälp från Stödlinjen på 020-81 91 00 — chatt och e-post finns också på stodlinjen.se. Vården kan dessutom hänvisa till specialistmottagningar för spelberoende.",
        ],
        paragraphs_en: [
          "Spelpaus is a tool, not a treatment. If you or someone close to you is struggling with gambling there is free and anonymous help from Stödlinjen at 020-81 91 00 — chat and email are also available at stodlinjen.se. Healthcare in Sweden can additionally refer you to specialist clinics for gambling addiction.",
        ],
      },
    ],
    takeaways_sv: [
      "Avstängningen gäller alla licensierade svenska operatörer.",
      "Du loggar in med BankID och väljer period 1–12 månader.",
      "Spelpaus täcker inte casinon utan svensk licens.",
      "Stödlinjen 020-81 91 00 erbjuder gratis och anonymt stöd.",
    ],
    takeaways_en: [
      "The block applies to every Swedish-licensed operator.",
      "You sign in with BankID and choose a period of 1–12 months.",
      "Spelpaus does not cover casinos without a Swedish license.",
      "Stödlinjen at 020-81 91 00 offers free and anonymous support.",
    ],
    faq: [
      {
        question_sv: "Kan jag avbryta avstängningen i förtid?",
        question_en: "Can I end the self-exclusion early?",
        answer_sv:
          "Nej. När en avstängning är aktiverad kan den inte hävas i förtid. Du måste vänta tills perioden löper ut.",
        answer_en:
          "No. Once a self-exclusion is active it cannot be lifted early. You must wait for the period to expire.",
      },
      {
        question_sv: "Påverkar Spelpaus min kreditvärdighet?",
        question_en: "Does Spelpaus affect my credit score?",
        answer_sv:
          "Nej. Spelpaus är inte ett kreditregister. Inga uppgifter om din avstängning delas med banker eller kreditupplysningsföretag.",
        answer_en:
          "No. Spelpaus is not a credit register. No information about your block is shared with banks or credit-rating agencies.",
      },
    ],
    updated_at: "2026-04-30",
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "guide-omsattningskrav",
    slug_sv: "omsattningskrav",
    slug_en: "wagering-requirements",
    category: "terms",
    title_sv: "Omsättningskrav — så fungerar de och så räknar du",
    title_en: "Wagering requirements — how they work and how to calculate them",
    excerpt_sv:
      "Omsättningskrav avgör hur många gånger du måste spela igenom en bonus innan vinsten är din.",
    excerpt_en:
      "Wagering requirements decide how many times you must wager a bonus before the winnings are yours to keep.",
    intro_sv:
      "Ett omsättningskrav är det villkor som binder en bonus till en viss insatsvolym. Det är casinots sätt att försäkra sig om att bonuspengar inte kan tas ut omedelbart utan att de faktiskt används till spel. Förstår du omsättningskravet förstår du också hur värdefull en bonus egentligen är.",
    intro_en:
      "A wagering requirement is the condition that ties a bonus to a certain volume of bets. It is the casino's way of making sure bonus money cannot be withdrawn immediately without actually being played. Understand the wagering requirement and you understand how valuable a bonus really is.",
    sections: [
      {
        heading_sv: "Hur räknar du ut omsättningen?",
        heading_en: "How do you calculate the wagering?",
        paragraphs_sv: [
          "Det vanligaste sättet att uttrycka omsättningskrav är ”Nx bonus” eller ”Nx (bonus + insättning)”. Säg att du sätter in 500 kr och får 500 kr i bonus, med ett krav på 30x bonus. Då måste du satsa 30 × 500 = 15 000 kr i spel innan vinsten kan tas ut. Med kravet 30x på (bonus + insättning) blir det istället 30 × 1000 = 30 000 kr.",
          "Hos svenska licensierade casinon finns dessutom en regel om att bonusvillkor ska vara tydliga och rättvisa, men du måste fortfarande själv läsa villkoren — kraven kan variera mellan operatörer.",
        ],
        paragraphs_en: [
          "The most common way to express a wagering requirement is ”Nx bonus” or ”Nx (bonus + deposit)”. Suppose you deposit 500 SEK and receive a 500 SEK bonus with a 30x bonus requirement. You must wager 30 × 500 = 15,000 SEK before withdrawing winnings. With 30x on (bonus + deposit) it becomes 30 × 1,000 = 30,000 SEK.",
          "At Swedish-licensed casinos there is an additional rule that bonus terms must be clear and fair, but you still need to read them — terms vary between operators.",
        ],
      },
      {
        heading_sv: "Spel räknar olika mycket",
        heading_en: "Games contribute differently",
        paragraphs_sv: [
          "De flesta casinon låter spelautomater bidra 100 procent till omsättningskravet. Bordsspel som blackjack och roulette bidrar ofta bara 5–10 procent — eller är helt undantagna. Livecasino, jackpotspel och progressiva slots har ofta egna regler. Läs alltid bonusvillkoren innan du börjar spela på något annat än slots.",
        ],
        paragraphs_en: [
          "Most casinos let slot games contribute 100 percent toward wagering. Table games such as blackjack and roulette typically contribute only 5–10 percent — or nothing at all. Live casino, jackpot games and progressive slots often have their own rules. Always read the bonus terms before playing anything other than slots with active bonus money.",
        ],
        list_sv: [
          "Slots: oftast 100 % bidrag.",
          "Blackjack och bordsspel: ofta 5–10 %, ibland 0 %.",
          "Live casino: varierar — kontrollera per sajt.",
          "Jackpotautomater: ofta uteslutna helt.",
        ],
        list_en: [
          "Slots: typically contribute 100%.",
          "Blackjack and table games: often 5–10%, sometimes 0%.",
          "Live casino: varies — check per site.",
          "Jackpot slots: often excluded entirely.",
        ],
      },
      {
        heading_sv: "Vanliga fällor",
        heading_en: "Common pitfalls",
        paragraphs_sv: [
          "Tre saker fäller flest spelare. Först: max insats per snurr. Många bonusvillkor sätter ett tak på 50 kr per spin. Bryter du mot det kan vinsten ogiltigförklaras. Sedan: tidsgräns. Bonusen kan löpa ut efter 7, 14 eller 30 dagar och då försvinner både bonus och vinst. Sist: förbjudna spel. Vissa slots från specifika studios kan vara helt undantagna även om resten räknar 100 procent.",
        ],
        paragraphs_en: [
          "Three things trip most players up. First: max bet per spin. Many bonus terms cap each spin at 50 SEK. Breach it and your winnings can be voided. Then: a time limit. The bonus may expire after 7, 14 or 30 days and at that point both bonus and any winnings are gone. Finally: forbidden games. Certain slots from specific studios may be excluded entirely even when the rest count 100 percent.",
        ],
      },
    ],
    takeaways_sv: [
      "30x bonus är ett vanligt riktvärde — kraven varierar.",
      "Slots bidrar oftast 100 % medan bordsspel bara bidrar lite.",
      "Max insats per spin är en fälla — håll dig under taket.",
      "Tidsgränsen kan radera hela vinsten om du missar den.",
    ],
    takeaways_en: [
      "30x bonus is a common benchmark — terms vary.",
      "Slots usually contribute 100% while table games contribute little.",
      "Max bet per spin is a trap — stay under the cap.",
      "The time limit can wipe out the entire bonus if you miss it.",
    ],
    faq: [
      {
        question_sv: "Vad är skillnaden mellan ”bonus” och ”bonus + insättning”?",
        question_en: "What is the difference between ”bonus” and ”bonus + deposit”?",
        answer_sv:
          "Det första räknar bara bonusbeloppet, det andra räknar både bonusen och din insättning. Den senare blir alltid ett dubbelt så stort omsättningskrav.",
        answer_en:
          "The first counts only the bonus amount, the second counts both the bonus and your deposit. The latter is always twice the wagering volume.",
      },
      {
        question_sv: "Är låga omsättningskrav alltid bättre?",
        question_en: "Are lower wagering requirements always better?",
        answer_sv:
          "Oftast ja, men kontrollera också tidsgräns, max insats och spelbidrag. En bonus med 10x krav men 24-timmars tidsgräns kan vara svårare än en bonus med 30x och 30 dagar.",
        answer_en:
          "Usually yes, but also check the time limit, max bet and game contribution. A 10x bonus with a 24-hour deadline can be harder to clear than a 30x bonus with 30 days.",
      },
    ],
    updated_at: "2026-04-30",
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "guide-rtp",
    slug_sv: "rtp",
    slug_en: "rtp",
    category: "terms",
    title_sv: "RTP — Return to Player förklarat",
    title_en: "RTP — Return to Player explained",
    excerpt_sv:
      "RTP är den teoretiska andel av insatserna som ett spel betalar tillbaka över tid.",
    excerpt_en:
      "RTP is the theoretical share of all bets a game pays back over time.",
    intro_sv:
      "RTP står för Return to Player. Det är den genomsnittliga procentsatsen av insatserna som ett spel betalar tillbaka till spelarna sett över hundratusentals snurr. Resten är husfördelen. RTP är ett av få objektiva mått du har när du jämför spel — men siffran säger inget om din enskilda session.",
    intro_en:
      "RTP stands for Return to Player. It is the average percentage of bets a game pays back to players across hundreds of thousands of spins. The remainder is the house edge. RTP is one of the few objective measures you have when comparing games — but it tells you nothing about a single session.",
    sections: [
      {
        heading_sv: "Hur tolkar du en RTP-siffra?",
        heading_en: "How do you read an RTP number?",
        paragraphs_sv: [
          "En slot med RTP 96,21 % betalar tillbaka i snitt 96,21 kr per 100 kr satsade — räknat över miljontals snurr. Det betyder att husets förväntade vinst är 3,79 kr per 100 kr i längden. Du som enskild spelare kan ändå få stora vinster eller stora förluster på en kväll, eftersom variansen är hög på korta tidsskalor.",
          "RTP är inte ett löfte. Den är en matematisk förväntan i en spelmotor som har testats och certifierats av tredjepartslabb som GLI eller eCOGRA. Hos svenska licensierade casinon är RTP dessutom kontrollerat av Spelinspektionen.",
        ],
        paragraphs_en: [
          "A slot with 96.21% RTP returns on average 96.21 SEK per 100 SEK wagered — averaged across millions of spins. That means the expected house win is 3.79 SEK per 100 SEK in the long run. As an individual player you can still see big wins or big losses in one session, because variance is high on short timescales.",
          "RTP is not a promise. It is a mathematical expectation in a game engine certified by third-party labs such as GLI or eCOGRA. At Swedish-licensed casinos the RTP is additionally supervised by Spelinspektionen.",
        ],
      },
      {
        heading_sv: "Vanliga RTP-intervall",
        heading_en: "Common RTP ranges",
        paragraphs_sv: [
          "De allra flesta videoslots har en RTP mellan 94 % och 97 %. Bordsspel som blackjack med korrekt strategi kan landa på över 99 %. Roulette ligger på 97,3 % (europeisk) eller 94,7 % (amerikansk, två nollor). Live game shows som Crazy Time har en lägre RTP, runt 95–96 %.",
        ],
        paragraphs_en: [
          "The vast majority of video slots sit between 94% and 97% RTP. Table games such as blackjack played with correct strategy can exceed 99%. Roulette is 97.3% (European) or 94.7% (American with two zeros). Live game shows like Crazy Time have a lower RTP, around 95–96%.",
        ],
        list_sv: [
          "Slots: typiskt 94–97 %.",
          "Blackjack med basstrategi: 99 %+",
          "Europeisk roulette: 97,3 %.",
          "Amerikansk roulette: 94,7 %.",
          "Live game shows: 95–96 %.",
        ],
        list_en: [
          "Slots: typically 94–97%.",
          "Blackjack with basic strategy: 99%+",
          "European roulette: 97.3%.",
          "American roulette: 94.7%.",
          "Live game shows: 95–96%.",
        ],
      },
      {
        heading_sv: "Vad RTP inte säger",
        heading_en: "What RTP does not tell you",
        paragraphs_sv: [
          "RTP säger inget om volatilitet (hur stora och hur ofta vinsterna är), bonusfunktionernas frekvens eller hur länge din bankroll håller. Två slots med 96 % RTP kan kännas radikalt olika: den ena ger ofta små vinster, den andra ger en stor träff var 200:e snurr.",
          "Vissa speltillverkare publicerar dessutom flera RTP-versioner för samma spel. En slot kan finnas med 96,21 %, 94 % och 92 % RTP, beroende på vad operatören väljer att aktivera. Kolla alltid spelets infosida hos casinot — den faktiska RTP:n står där.",
        ],
        paragraphs_en: [
          "RTP says nothing about volatility (how big and how often wins land), bonus-feature frequency or how long your bankroll lasts. Two slots with 96% RTP can feel radically different: one drips small wins, the other pays a large hit roughly every 200 spins.",
          "Some studios additionally publish several RTP versions of the same game. A slot might exist at 96.21%, 94% and 92%, depending on what the operator activates. Always check the game's info screen at the casino — the actual RTP is shown there.",
        ],
      },
    ],
    takeaways_sv: [
      "RTP är ett genomsnitt över miljontals snurr, inte en garanti.",
      "Du måste läsa både RTP och volatilitet för att jämföra spel.",
      "Vissa spel har flera RTP-versioner — kolla alltid speldetaljerna.",
      "Husfördelen är 100 % minus RTP.",
    ],
    takeaways_en: [
      "RTP is an average across millions of spins, not a guarantee.",
      "Read both RTP and volatility to compare games meaningfully.",
      "Some games ship in multiple RTP versions — always check the info screen.",
      "House edge equals 100% minus RTP.",
    ],
    faq: [
      {
        question_sv: "Är hög RTP samma sak som lätt att vinna?",
        question_en: "Does high RTP mean easy to win?",
        answer_sv:
          "Nej. Hög RTP betyder bara att en större andel av insatserna betalas tillbaka över tid. Du kan fortfarande ha en lång förlustperiod på en hög-RTP-slot.",
        answer_en:
          "No. High RTP only means a larger share of bets is returned over time. You can still have a long losing run on a high-RTP slot.",
      },
      {
        question_sv: "Hur kontrollerar jag RTP innan jag spelar?",
        question_en: "How do I verify RTP before I play?",
        answer_sv:
          "Öppna spelets infomeny inifrån casinot. Den visar aktuell RTP-version. Speltillverkarens egen webbplats listar oftast den högsta RTP-varianten.",
        answer_en:
          "Open the game's info menu inside the casino. It shows the active RTP version. The studio's own website usually lists the highest RTP variant.",
      },
    ],
    updated_at: "2026-04-30",
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "guide-volatility",
    slug_sv: "volatilitet",
    slug_en: "volatility",
    category: "terms",
    title_sv: "Volatilitet i spelautomater — låg, medel och hög varians",
    title_en: "Slot volatility — low, medium and high variance",
    excerpt_sv:
      "Volatilitet beskriver hur ofta och hur stora vinsterna är i en spelautomat.",
    excerpt_en:
      "Volatility describes how often and how big slot wins are.",
    intro_sv:
      "Två slots med samma RTP kan ge helt olika spelupplevelser. Skillnaden ligger i volatiliteten — också kallad varians. Den beskriver formen på vinstkurvan: om du får många små vinster eller få men stora. Att förstå volatilitet är nyckeln till att välja rätt slot för din bankroll.",
    intro_en:
      "Two slots with identical RTP can produce wildly different play experiences. The difference is volatility — also called variance. It describes the shape of the win curve: many small wins, or rare but large ones. Understanding volatility is the key to picking the right slot for your bankroll.",
    sections: [
      {
        heading_sv: "Tre nivåer i praktiken",
        heading_en: "Three levels in practice",
        paragraphs_sv: [
          "Lågvolatila slots ger ofta små vinster. De är förlåtande, drar långsamt på bankrollen och passar för längre sessioner. Starburst är ett klassiskt exempel.",
          "Medelvolatila slots balanserar små och medelstora vinster. Gonzo's Quest hör hit — vinsterna är inte alltid små, men inte heller långt ifrån varandra.",
          "Högvolatila slots har långa torrperioder följda av stora träffar. Book of Dead, Wanted Dead or a Wild och nyare nolimit-slots är typexempel. Bankrollen måste tåla flera hundra snurr utan utdelning.",
        ],
        paragraphs_en: [
          "Low-volatility slots pay small wins frequently. They are forgiving, drain the bankroll slowly and suit longer sessions. Starburst is a classic example.",
          "Medium-volatility slots balance small and medium wins. Gonzo's Quest fits here — wins are not always small, but not far apart.",
          "High-volatility slots have long dry spells followed by big hits. Book of Dead, Wanted Dead or a Wild and newer Nolimit titles are typical. The bankroll must withstand several hundred spins without payout.",
        ],
      },
      {
        heading_sv: "Hur väljer du rätt volatilitet?",
        heading_en: "How do you pick the right volatility?",
        paragraphs_sv: [
          "Räkna bakåt från din session. Om du har 500 kr och vill spela en timme på 1 kr-spinn behöver du en lågvolatil slot, annars är pengarna slut långt innan sessionen är klar. Om du har 2000 kr och jagar en stor vinst i bonusrundan kan en högvolatil slot vara rätt — men räkna med stora svängningar.",
          "Volatilitet matchar också bonusvillkor. Ett 30x omsättningskrav på 1000 kr betyder 30 000 kr i insatser. På högvolatil slot är risken stor att du förlorar bonusen i en torrperiod innan du når kravet. Lägre volatilitet ger mer förutsägbar bonusgenomspelning.",
        ],
        paragraphs_en: [
          "Reverse-engineer from your session. If you have 500 SEK and want an hour at 1 SEK spins, you need a low-volatility slot or the bankroll is gone long before the session ends. If you have 2,000 SEK chasing a bonus-round hit, a high-volatility slot can be right — but expect huge swings.",
          "Volatility also interacts with bonus terms. A 30x wagering requirement on 1,000 SEK means 30,000 SEK in stakes. On a high-volatility slot the risk is real that the bonus dies in a dry spell before clearing. Lower volatility gives a more predictable wager-through.",
        ],
      },
    ],
    takeaways_sv: [
      "Lågvolatil = ofta små vinster, snäll mot bankrollen.",
      "Högvolatil = sällsynta men stora träffar, kräver tålamod.",
      "Matcha volatilitet mot session-budget och bonusvillkor.",
      "Volatilitet påverkar bonusgenomspelningens svårighet.",
    ],
    takeaways_en: [
      "Low volatility = frequent small wins, gentle on the bankroll.",
      "High volatility = rare but large hits, demands patience.",
      "Match volatility to your session budget and bonus terms.",
      "Volatility shapes how hard a bonus is to clear.",
    ],
    faq: [
      {
        question_sv: "Står volatiliteten alltid i spelet?",
        question_en: "Is volatility always shown in the game?",
        answer_sv:
          "De flesta studios anger volatilitet i spelets infomeny, ofta som ett 1-5 stjärnsbetyg. Vissa äldre spel saknar uppgiften.",
        answer_en:
          "Most studios show volatility in the game's info menu, often as a 1–5 star rating. Some older games omit it.",
      },
    ],
    updated_at: "2026-04-30",
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "guide-blackjack",
    slug_sv: "blackjack-regler",
    slug_en: "blackjack-rules",
    category: "rules",
    title_sv: "Blackjack-regler — komplett guide för 2026",
    title_en: "Blackjack rules — complete 2026 guide",
    excerpt_sv:
      "Lär dig hur blackjack spelas, basstrategi, sidobett och varför det är ett av casinots smartaste spel.",
    excerpt_en:
      "Learn how blackjack is played, basic strategy, side bets and why it is one of the smartest games in the casino.",
    intro_sv:
      "Blackjack är ett av få casinospel där dina beslut faktiskt påverkar oddsen. Med korrekt basstrategi kan husfördelen pressas under en halv procent. Det gör blackjack till ett av de mest matematiskt rättvisa spelen i casinot — under förutsättning att du faktiskt lär dig spela rätt.",
    intro_en:
      "Blackjack is one of the few casino games where your decisions actually move the odds. With correct basic strategy the house edge can be squeezed below half a percent. That makes blackjack one of the most mathematically fair games in the casino — provided you actually learn to play correctly.",
    sections: [
      {
        heading_sv: "Spelets gång",
        heading_en: "How a hand plays",
        paragraphs_sv: [
          "Du och dealern får varsina två kort. Ditt mål är att komma närmare 21 än dealern, utan att gå över. Klädda kort räknas som 10. Ess räknas som 1 eller 11. Dealern visar ett kort öppet och ett dolt. Sedan bestämmer du om du vill ta fler kort (Hit), stanna (Stand), dubbla insatsen (Double) eller dela ett par (Split).",
          "När alla spelare är klara vänder dealern sitt dolda kort. Dealern måste själv följa fasta regler: dra till 17 eller högre, stanna på 17 (på vissa bord stannar dealern även på Soft 17). Vinner du över dealerns hand utan att gå över 21 får du tillbaka 1:1. En blackjack — ess plus en tia eller bildkort på första två kort — betalar normalt 3:2.",
        ],
        paragraphs_en: [
          "You and the dealer each receive two cards. Your goal is to land closer to 21 than the dealer without going over. Face cards count as 10. The ace counts as 1 or 11. The dealer shows one card up and one face-down. You then decide whether to take more cards (Hit), stand, double down or split a pair.",
          "Once all players are done the dealer reveals the down card. The dealer follows fixed rules: draw to 17 or higher, stand on 17 (some tables also have the dealer stand on Soft 17). Beat the dealer without busting and you are paid 1:1. A blackjack — ace plus a 10-value card on the first two cards — typically pays 3:2.",
        ],
      },
      {
        heading_sv: "Dina handlingar i detalj",
        heading_en: "Your actions in detail",
        paragraphs_sv: [
          "Hit: ta ett kort till. Stand: behåll handen som den är. Double Down: dubbla insatsen och få exakt ett kort till. Split: dela ett par till två separata händer (kostar en till insats). Surrender (om regeln finns): kasta handen och få tillbaka halva insatsen — användbart vid hårda 16 mot dealerns 9, 10 eller ess.",
          "Insurance erbjuds när dealern har ett ess. Det är ett sidobett som betalar 2:1 om dealern har blackjack. Räkna inte med försäkringen som värdeskapare — på ett vanligt 6-kortsdäck är husfördelen på Insurance ungefär 7 procent.",
        ],
        paragraphs_en: [
          "Hit: take another card. Stand: keep your hand. Double Down: double the bet and receive exactly one more card. Split: divide a pair into two hands (costs an extra bet). Surrender (if available): fold and get half your bet back — useful on hard 16 versus dealer 9, 10 or ace.",
          "Insurance is offered when the dealer shows an ace. It is a side bet that pays 2:1 if the dealer has blackjack. Insurance is not a value play — on a standard six-deck shoe the house edge on insurance is about 7%.",
        ],
      },
      {
        heading_sv: "Basstrategi i tre rader",
        heading_en: "Basic strategy in three lines",
        paragraphs_sv: [
          "Det finns ett komplett basstrategischema som listar rätt drag för varje kombination av spelarhand och dealerns synliga kort. Memorera den om du spelar regelbundet — den minimerar husfördelen utan kortlagsräkning.",
        ],
        paragraphs_en: [
          "A complete basic strategy chart lists the correct play for every combination of your hand and the dealer's up card. Memorise it if you play regularly — it minimises the house edge without card counting.",
        ],
        list_sv: [
          "Stå på hård 17 och uppåt.",
          "Dubbla på 11 mot allt utom dealerns ess.",
          "Splitta alltid ess och åttor.",
          "Splitta aldrig 4:or, 5:or eller 10:or.",
          "Dra på Soft 17 mot dealer 7+, stå annars.",
        ],
        list_en: [
          "Stand on hard 17 or higher.",
          "Double on 11 against anything except a dealer ace.",
          "Always split aces and eights.",
          "Never split fours, fives or tens.",
          "Hit Soft 17 against dealer 7 or higher, otherwise stand.",
        ],
      },
      {
        heading_sv: "Sidobett och varianter",
        heading_en: "Side bets and variants",
        paragraphs_sv: [
          "Perfect Pairs, 21+3 och Lucky Lucky är populära sidobett, men husfördelen ligger ofta på 3–8 procent — flera gånger högre än grundspelet. De höjer underhållningen men sänker ditt långsiktiga värde markant.",
          "Vanliga varianter du möter är European Blackjack (ingen hole-card för dealern), Spanish 21 (alla 10:or borttagna ur leken) och Blackjack Switch (du kan byta toppkortet mellan två händer). Reglerna kring Double, Split och dealer-Soft-17 påverkar husfördelen mer än namnet på varianten.",
        ],
        paragraphs_en: [
          "Perfect Pairs, 21+3 and Lucky Lucky are popular side bets, but their house edge is often 3–8 percent — several times the base game. They add entertainment but materially lower your long-term value.",
          "Common variants you will meet include European Blackjack (no dealer hole card), Spanish 21 (all tens removed) and Blackjack Switch (swap the top cards between two hands). Rules around Double, Split and dealer Soft-17 move the house edge far more than the variant name.",
        ],
      },
    ],
    takeaways_sv: [
      "Med basstrategi pressas husfördelen under 0,5 %.",
      "En blackjack betalar 3:2 — undvik bord som betalar 6:5.",
      "Insurance och sidobett har hög husfördel.",
      "Splitta ess och åttor, dubbla på 11.",
    ],
    takeaways_en: [
      "Basic strategy pushes the house edge below 0.5%.",
      "A blackjack should pay 3:2 — avoid 6:5 tables.",
      "Insurance and side bets have a high house edge.",
      "Split aces and eights, double on 11.",
    ],
    faq: [
      {
        question_sv: "Är kortlagsräkning olagligt online?",
        question_en: "Is card counting illegal online?",
        answer_sv:
          "Kortlagsräkning är inte olagligt, men online blandas leken om varje hand i RNG-blackjack och varje runda i de flesta livecasinon. Det gör räkning praktiskt omöjlig.",
        answer_en:
          "Card counting is not illegal, but online RNG blackjack reshuffles each hand and most live tables shuffle each round. That makes counting practically impossible.",
      },
      {
        question_sv: "Räknar blackjack som omsättning på en bonus?",
        question_en: "Does blackjack count toward bonus wagering?",
        answer_sv:
          "Oftast bara 5–10 procent av insatsen räknas. Vissa casinon utesluter blackjack helt från bonusspelet. Läs villkoren.",
        answer_en:
          "Typically only 5–10 percent of the wager counts. Some casinos exclude blackjack entirely from bonus play. Read the terms.",
      },
    ],
    updated_at: "2026-04-30",
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "guide-roulette",
    slug_sv: "roulette-regler",
    slug_en: "roulette-rules",
    category: "rules",
    title_sv: "Roulette-regler — så fungerar europeisk, fransk och amerikansk roulette",
    title_en: "Roulette rules — how European, French and American roulette work",
    excerpt_sv:
      "Allt du behöver veta om roulette: hjulets layout, satsningar, husfördel och vilken variant som ger bäst odds.",
    excerpt_en:
      "Everything you need to know about roulette: wheel layout, bets, house edge and which variant gives the best odds.",
    intro_sv:
      "Roulette är ett av världens äldsta casinospel — namnet betyder ”litet hjul” på franska. Spelet ser enkelt ut men gömmer en värld av satsningskombinationer, varianter och husfördelar som skiljer sig mer än de flesta tror. Den här guiden förklarar hur du spelar, vilka satsningar som finns och vilken variant du bör välja.",
    intro_en:
      "Roulette is one of the world's oldest casino games — the name means ”little wheel” in French. The game looks simple but hides a world of bet combinations, variants and house edges that differ more than most players realise. This guide explains how to play, what bets exist and which variant you should pick.",
    sections: [
      {
        heading_sv: "Hjulet och bordet",
        heading_en: "The wheel and the table",
        paragraphs_sv: [
          "Ett rouletthjul har numren 1 till 36 plus en grön nolla. Numren är omväxlande röda och svarta. Krupiern snurrar hjulet åt ett håll och kastar en boll åt motsatt håll. När bollen stannar i en ficka avgörs alla satsningar på bordet.",
          "På den europeiska varianten finns en nolla. På den amerikanska varianten finns både 0 och 00 — vilket dubblar husfördelen från 2,7 % till 5,26 %. Den franska varianten har en nolla och en regel som heter La Partage som ofta halverar förlusten på jämna pengar-satsningar när bollen landar på noll.",
        ],
        paragraphs_en: [
          "A roulette wheel has numbers 1 to 36 plus a green zero. The numbers alternate red and black. The croupier spins the wheel one way and rolls the ball the other. When the ball settles in a pocket every bet on the table is resolved.",
          "European roulette has one zero. American roulette has both 0 and 00 — doubling the house edge from 2.7% to 5.26%. French roulette has one zero plus a rule called La Partage that often halves the loss on even-money bets when the ball lands on zero.",
        ],
      },
      {
        heading_sv: "Inside bets — höga utbetalningar, låg träffchans",
        heading_en: "Inside bets — high payout, low hit rate",
        paragraphs_sv: [
          "Inside bets ligger på själva nummerrutan. De har höga utbetalningar men träffar mer sällan. Du kan satsa på ett enskilt nummer (Straight Up), två angränsande nummer (Split), tre nummer på en rad (Street), fyra nummer i ett hörn (Corner), sex nummer i två rader (Six Line) eller en topplinje (Top Line — bara på amerikansk).",
        ],
        paragraphs_en: [
          "Inside bets sit on the number grid itself. They have high payouts but hit rarely. You can bet on a single number (Straight Up), two adjacent numbers (Split), three in a row (Street), four at a corner (Corner), six in two rows (Six Line) or a top line (Top Line — American only).",
        ],
        list_sv: [
          "Straight Up — 35:1, träffchans 2,7 %.",
          "Split — 17:1, träffchans 5,4 %.",
          "Street — 11:1, träffchans 8,1 %.",
          "Corner — 8:1, träffchans 10,8 %.",
          "Six Line — 5:1, träffchans 16,2 %.",
        ],
        list_en: [
          "Straight Up — 35:1, hit rate 2.7%.",
          "Split — 17:1, hit rate 5.4%.",
          "Street — 11:1, hit rate 8.1%.",
          "Corner — 8:1, hit rate 10.8%.",
          "Six Line — 5:1, hit rate 16.2%.",
        ],
      },
      {
        heading_sv: "Outside bets — säkrare, lägre utbetalningar",
        heading_en: "Outside bets — safer, lower payouts",
        paragraphs_sv: [
          "Outside bets ligger på fälten utanför nummerrutan. De flesta är jämna pengar-satsningar: rött eller svart, jämnt eller udda, lågt (1–18) eller högt (19–36). Du kan också satsa på en kolumn eller en sjättedel (1st, 2nd eller 3rd 12). Jämna pengar-satsningar betalar 1:1, kolumn och dussin betalar 2:1.",
          "På fransk roulette gäller La Partage på jämna pengar-satsningar. Hamnar bollen på noll får du tillbaka halva insatsen i stället för att förlora hela. Det sänker husfördelen på de satsningarna till 1,35 % — vilket är en av casinots bästa odds.",
        ],
        paragraphs_en: [
          "Outside bets sit on the fields outside the number grid. Most are even-money: red or black, odd or even, low (1–18) or high (19–36). You can also bet a column or a dozen (1st, 2nd or 3rd 12). Even-money bets pay 1:1, columns and dozens pay 2:1.",
          "French roulette applies La Partage on even-money bets. If the ball lands on zero you get half your stake back instead of losing it all. That drops the house edge on those bets to 1.35% — one of the best odds in the casino.",
        ],
      },
      {
        heading_sv: "Vanliga strategier (och varför ingen slår huset)",
        heading_en: "Common strategies (and why none beats the house)",
        paragraphs_sv: [
          "Martingale dubblar insatsen efter varje förlust. Den fungerar tills du når bordets max — eller tar slut på pengar. D'Alembert höjer insatsen med en enhet efter förlust och sänker med en efter vinst. Fibonacci följer talföljden 1, 1, 2, 3, 5, 8 osv. Alla tre är progressioner: de förändrar inte spelets matematiska husfördel, de förändrar bara mönstret av insatser.",
          "Det enda sätt som långsiktigt sänker husfördelen är att spela varianten med lägst inbyggd husfördel — fransk roulette med La Partage på jämna pengar — och hålla sig till den.",
        ],
        paragraphs_en: [
          "Martingale doubles your bet after each loss. It works until you hit the table max — or run out of money. D'Alembert raises one unit after a loss and lowers one after a win. Fibonacci follows the 1, 1, 2, 3, 5, 8 sequence. All three are progressions: they do not change the game's mathematical edge, only the pattern of bets.",
          "The only long-term way to lower the house edge is to play the variant with the lowest built-in edge — French roulette with La Partage on even-money bets — and stick to it.",
        ],
      },
    ],
    takeaways_sv: [
      "Europeisk roulette har 2,7 % husfördel, amerikansk 5,26 %.",
      "Fransk roulette med La Partage = bara 1,35 % på jämna pengar.",
      "Inside bets ger 35:1 men träffar bara 2,7 % av tiden.",
      "Progressioner som Martingale ändrar inte husfördelen.",
    ],
    takeaways_en: [
      "European roulette has a 2.7% edge, American 5.26%.",
      "French roulette with La Partage = just 1.35% on even-money bets.",
      "Inside bets pay 35:1 but only hit 2.7% of the time.",
      "Progressions like Martingale do not change the house edge.",
    ],
    faq: [
      {
        question_sv: "Vilken roulette ska jag välja?",
        question_en: "Which roulette should I choose?",
        answer_sv:
          "Fransk roulette med La Partage först, europeisk roulette i andra hand. Undvik amerikansk om det går — den dubbla nollan dubblar husfördelen.",
        answer_en:
          "French roulette with La Partage first, European roulette second. Avoid American if you can — the double zero doubles the house edge.",
      },
      {
        question_sv: "Är livecasino-roulette annorlunda?",
        question_en: "Is live casino roulette different?",
        answer_sv:
          "Reglerna är identiska men hjulet är fysiskt och spinnen är långsammare. Vissa bord lägger till multiplikatorer (t.ex. Lightning Roulette) som ändrar matematiken något.",
        answer_en:
          "The rules are identical but the wheel is physical and spins are slower. Some tables add multipliers (e.g. Lightning Roulette) that change the maths slightly.",
      },
    ],
    updated_at: "2026-04-30",
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "guide-baccarat",
    slug_sv: "baccarat-regler",
    slug_en: "baccarat-rules",
    category: "rules",
    title_sv: "Baccarat-regler — Banker, Player och Tie förklarat",
    title_en: "Baccarat rules — Banker, Player and Tie explained",
    excerpt_sv:
      "Baccarat är ett enkelt kortspel med tre möjliga satsningar och en av casinots lägsta husfördelar.",
    excerpt_en:
      "Baccarat is a simple card game with three possible bets and one of the lowest house edges in the casino.",
    intro_sv:
      "Baccarat är världens kanske mest hyllade casinospel bland matematiker. Det är enkelt, snabbt och har en av de allra lägsta husfördelarna när du spelar Banker. Trots det är spelet relativt okänt i Sverige jämfört med blackjack och roulette. Den här guiden lär dig allt du behöver veta för att hänga med vid bordet.",
    intro_en:
      "Baccarat is arguably the casino game most celebrated by mathematicians. It is simple, fast, and has one of the lowest house edges when you bet Banker. Despite that it is less well known in Sweden than blackjack and roulette. This guide teaches you everything you need to follow along at the table.",
    sections: [
      {
        heading_sv: "Spelets idé",
        heading_en: "The basic idea",
        paragraphs_sv: [
          "Baccarat handlar inte om att slå dealern. Du satsar på vilken av två händer — Banker eller Player — som kommer närmast 9. Du kan också satsa på Tie (oavgjort). Två kort delas ut till varje hand, ibland tre. Klädda kort och tior räknas som 0. Ess är 1. Andra kort är sitt nominella värde.",
          "Om summan blir tvåsiffrig stryks tiotalet — 7 + 8 = 15 räknas som 5. Fasta dragningsregler avgör om en tredje kort delas ut, så du behöver inte fatta beslut. Det är därför baccarat är extremt nybörjarvänligt.",
        ],
        paragraphs_en: [
          "Baccarat is not about beating the dealer. You bet which of two hands — Banker or Player — will land closer to 9. You can also bet Tie. Each hand receives two cards, sometimes three. Face cards and tens count as 0. Aces are 1. Other cards count their face value.",
          "If the total is two digits, the tens digit is dropped — 7 + 8 = 15 counts as 5. Fixed drawing rules determine whether a third card is dealt, so you do not have to make decisions. That is why baccarat is extremely beginner-friendly.",
        ],
      },
      {
        heading_sv: "Husfördelen per satsning",
        heading_en: "The house edge per bet",
        paragraphs_sv: [
          "Banker-satsningen har historiskt sett lägst husfördel — runt 1,06 procent — men casinot tar 5 procent kommission på vinster för att kompensera. Player-satsningen har en husfördel på cirka 1,24 procent. Tie-satsningen ser frestande ut med 8:1 i utbetalning men husfördelen är hela 14,4 procent. Undvik Tie.",
        ],
        paragraphs_en: [
          "The Banker bet historically has the lowest house edge — around 1.06% — but the casino takes a 5% commission on wins to offset it. The Player bet has roughly a 1.24% edge. The Tie bet looks tempting at 8:1 but the house edge is a whopping 14.4%. Avoid Tie.",
        ],
        list_sv: [
          "Banker — husfördel 1,06 %, kommission 5 %.",
          "Player — husfördel 1,24 %, ingen kommission.",
          "Tie — utbetalning 8:1, husfördel 14,4 %.",
        ],
        list_en: [
          "Banker — house edge 1.06%, 5% commission.",
          "Player — house edge 1.24%, no commission.",
          "Tie — pays 8:1, house edge 14.4%.",
        ],
      },
      {
        heading_sv: "Sidobett och varianter",
        heading_en: "Side bets and variants",
        paragraphs_sv: [
          "Player Pair, Banker Pair, Big och Small är vanliga sidobett. De har högre utbetalning än grundspelet men också högre husfördel. Squeeze Baccarat gör spelet långsammare med ceremoniella drag. Speed Baccarat dealar varje runda på 27 sekunder. Punto Banco är samma spel under ett annat namn.",
        ],
        paragraphs_en: [
          "Player Pair, Banker Pair, Big and Small are common side bets. They pay more than the main game but carry a higher house edge. Squeeze Baccarat slows the game with ceremonial reveals. Speed Baccarat deals each round in 27 seconds. Punto Banco is the same game under a different name.",
        ],
      },
    ],
    takeaways_sv: [
      "Banker har lägst husfördel — runt 1 % efter kommission.",
      "Tie ser fett ut men är casinots dyraste sidobett.",
      "Du behöver inte fatta beslut om en tredje kort.",
      "Speed Baccarat ger 100+ ronder i timmen — håll koll på bankrollen.",
    ],
    takeaways_en: [
      "Banker has the lowest edge — around 1% after commission.",
      "Tie looks rich but is the casino's most expensive side bet.",
      "You never decide whether to draw a third card.",
      "Speed Baccarat hits 100+ rounds an hour — watch your bankroll.",
    ],
    faq: [
      {
        question_sv: "Räknas baccarat som omsättning på bonus?",
        question_en: "Does baccarat count toward bonus wagering?",
        answer_sv:
          "Oftast bara 10 procent eller mindre, ibland helt undantaget. Läs alltid bonusvillkoren.",
        answer_en:
          "Usually only 10% or less, sometimes excluded entirely. Always read the bonus terms.",
      },
    ],
    updated_at: "2026-04-30",
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "guide-poker",
    slug_sv: "poker-regler",
    slug_en: "poker-rules",
    category: "rules",
    title_sv: "Poker-regler — Texas Hold'em från grunden",
    title_en: "Poker rules — Texas Hold'em from the ground up",
    excerpt_sv:
      "Hur No Limit Texas Hold'em fungerar, vilka händer som slår vad och en grundläggande strategi.",
    excerpt_en:
      "How No Limit Texas Hold'em works, what hands beat what and a basic strategy primer.",
    intro_sv:
      "Texas Hold'em är pokerns mest spelade variant och basen för World Series of Poker. Du får två dolda kort, fem gemensamma kort delas ut i tre rundor, och du försöker bilda den bästa fem-kortshanden. Reglerna är enkla — strategin är det som gör spelet livslångt.",
    intro_en:
      "Texas Hold'em is poker's most played variant and the backbone of the World Series of Poker. You get two private hole cards, five community cards are dealt in three rounds, and you try to build the best five-card hand. The rules are simple — the strategy is what makes the game lifelong.",
    sections: [
      {
        heading_sv: "Spelets ronder",
        heading_en: "The four streets",
        paragraphs_sv: [
          "Pre-flop: alla får två hole cards. Spelaren till vänster om dealerknappen lägger small blind, nästa lägger big blind. Sedan agerar spelarna i tur och ordning: fold, call eller raise. Flop: tre gemensamma kort. Ny budgivningsrunda. Turn: ett fjärde gemensamt kort. Ny rond. River: ett femte och sista kort. Sista rond — sedan visas händerna och högsta vinner.",
          "I No Limit-versionen kan du satsa allt du har när du vill. I Limit-versionen är insatsen fast. Pot-Limit Omaha (PLO) är en annan populär variant där du får fyra hole cards och måste använda exakt två av dem.",
        ],
        paragraphs_en: [
          "Pre-flop: every player gets two hole cards. The player left of the dealer button posts the small blind, next the big blind. Then players act in turn: fold, call or raise. Flop: three community cards. New betting round. Turn: a fourth community card. Another round. River: a fifth and final community card. Final round — then hands are shown and the best wins.",
          "No Limit lets you bet your entire stack any time. Limit caps each bet. Pot-Limit Omaha (PLO) is another popular variant where you receive four hole cards and must use exactly two of them.",
        ],
      },
      {
        heading_sv: "Handrankning från högst till lägst",
        heading_en: "Hand ranking from high to low",
        paragraphs_sv: [
          "Royal Flush — A, K, Q, J, 10 i samma färg. Straight Flush — fem kort i ordning, samma färg. Four of a Kind — fyra lika. Full House — tre lika plus ett par. Flush — fem kort i samma färg. Straight — fem kort i ordning. Three of a Kind — tre lika. Two Pair — två par. One Pair — ett par. High Card — högsta enskilda kort.",
        ],
        paragraphs_en: [
          "Royal Flush — A, K, Q, J, 10 of the same suit. Straight Flush — five suited cards in sequence. Four of a Kind. Full House — three of a kind plus a pair. Flush — five cards same suit. Straight — five in sequence. Three of a Kind. Two Pair. One Pair. High Card.",
        ],
      },
      {
        heading_sv: "Position, position, position",
        heading_en: "Position, position, position",
        paragraphs_sv: [
          "I Hold'em är platsen vid bordet en av de viktigaste faktorerna. Att agera sist (button) ger dig informationen om vad alla andra gjort innan du fattar beslut. Tidiga positioner (under the gun) tvingar dig att agera blint mot resten av bordet. Spela tightare i tidig position och bredare i sen position — det är grunden i alla moderna pokerstrategier.",
        ],
        paragraphs_en: [
          "Position at the table is one of the most important factors in Hold'em. Acting last (button) gives you information about every other player before you decide. Early positions (under the gun) force you to act blind to the rest of the table. Play tighter in early position and wider in late position — that is the foundation of every modern poker strategy.",
        ],
      },
      {
        heading_sv: "Cash game eller turnering?",
        heading_en: "Cash game or tournament?",
        paragraphs_sv: [
          "Cash games har fasta blinds och du köper in dig direkt — fickorna byts mot riktiga pengar när du går. Turneringar har ett fast inköp, alla startar med samma stack och blinds höjs gradvis. Vinnaren får största delen av prispotten. Båda formerna kräver olika strategi: cash game premierar tålamod, turnering premierar timing.",
        ],
        paragraphs_en: [
          "Cash games use fixed blinds and chips trade directly for cash when you leave. Tournaments use a fixed buy-in, every player starts with the same stack and blinds rise over time. The winner takes the bulk of the prize pool. The two formats need different strategies: cash rewards patience, tournaments reward timing.",
        ],
      },
    ],
    takeaways_sv: [
      "Hold'em har fyra rundor: pre-flop, flop, turn, river.",
      "Position ger information — sista positionen är guld.",
      "No Limit, Limit och Pot-Limit påverkar strategin radikalt.",
      "Cash games belönar tålamod, turneringar belönar timing.",
    ],
    takeaways_en: [
      "Hold'em has four streets: pre-flop, flop, turn, river.",
      "Position gives information — last position is gold.",
      "No Limit, Limit and Pot-Limit shift strategy dramatically.",
      "Cash games reward patience, tournaments reward timing.",
    ],
    faq: [
      {
        question_sv: "Är poker tillåtet i Sverige?",
        question_en: "Is poker legal in Sweden?",
        answer_sv:
          "Ja, hos operatörer med svensk licens. Spelinspektionen reglerar både cash games och turneringar online.",
        answer_en:
          "Yes, at operators with a Swedish license. Spelinspektionen regulates both cash games and tournaments online.",
      },
    ],
    updated_at: "2026-04-30",
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "guide-bankroll",
    slug_sv: "bankroll-management",
    slug_en: "bankroll-management",
    category: "responsible",
    title_sv: "Bankroll management — så håller du spelandet hållbart",
    title_en: "Bankroll management — keep your play sustainable",
    excerpt_sv:
      "Hantera din spelkassa, sätt gränser och spela på en nivå du har råd med.",
    excerpt_en:
      "Manage your bankroll, set limits and play at a level you can afford.",
    intro_sv:
      "Bankroll management är inte en strategi för att vinna mer. Det är en strategi för att spela längre, lugnare och inte tappa kontrollen. Hos svenska licensierade casinon är insättningsgräns obligatorisk — men den ersätter inte en plan för hur du fördelar pengar mellan sessioner.",
    intro_en:
      "Bankroll management is not a strategy to win more. It is a strategy to play longer, calmer and without losing control. Swedish-licensed casinos enforce a deposit limit — but that does not replace your own plan for how to spread money across sessions.",
    sections: [
      {
        heading_sv: "Sätt en månadsbudget",
        heading_en: "Set a monthly budget",
        paragraphs_sv: [
          "Bestäm i god tid hur mycket du har råd att förlora på spel under en månad. Räkna med att du i teorin förlorar hela beloppet — det är så husfördelen fungerar. Beloppet ska aldrig komma från lön avsedd för boende, mat eller räkningar.",
          "När månadsbudgeten är satt, dela den i veckobudget eller sessionsbudget. En vanlig modell är 25 procent per vecka, plus en buffert för ett särskilt event eller turnering om du brukar spela något sådant.",
        ],
        paragraphs_en: [
          "Decide well ahead of time how much you can afford to lose on gambling in a month. Treat the figure as if it disappears entirely — that is how the house edge works. The amount must never come from money earmarked for rent, food or bills.",
          "Once the monthly budget is set, divide it into weekly or session budgets. A common pattern is 25% per week, plus a buffer for a specific event or tournament if you typically play those.",
        ],
      },
      {
        heading_sv: "Insatsstorlek mot bankroll",
        heading_en: "Bet size relative to bankroll",
        paragraphs_sv: [
          "Tumregeln är 1 till 2 procent av din sessionsbankroll per insats. Med 500 kr i kassan betyder det 5–10 kr per spin på en slot, eller en blackjack-insats om 5–10 kr. Det säkerställer att du har tillräckligt med snurr eller händer för att variansen ska jämnas ut.",
          "På högvolatila slots kan det vara klokt att hålla sig i den nedre delen av spannet, eftersom torrperioderna är längre. På bordsspel med låg husfördel kan du hålla dig i den övre delen — men inte högre.",
        ],
        paragraphs_en: [
          "The rule of thumb is 1–2% of your session bankroll per bet. With 500 SEK in the bankroll that means 5–10 SEK per spin on a slot, or a blackjack bet of 5–10 SEK. It ensures you have enough spins or hands for variance to even out.",
          "On high-volatility slots it is wise to stay at the low end of the range because dry spells are longer. On low-edge table games you can sit at the high end — but no higher.",
        ],
      },
      {
        heading_sv: "Sätt vinst- och förluststopp",
        heading_en: "Set win and loss stops",
        paragraphs_sv: [
          "Bestäm i förväg vad du gör när bankrollen dubblas eller halveras. Vanlig modell: ta hem hela vinsten över 50 procent och fortsätt med ursprungsbankrollen. Vid 50 procents förlust avsluta sessionen helt och kom tillbaka en annan dag. Det här är inte en magisk vinststrategi — det är en kontrollstrategi.",
        ],
        paragraphs_en: [
          "Decide in advance what you do when the bankroll doubles or halves. A common pattern: bank any winnings beyond +50% and continue with the original bankroll. At −50% end the session entirely and come back another day. This is not a magic winning system — it is a control system.",
        ],
      },
    ],
    takeaways_sv: [
      "Sätt månadsbudget innan du börjar spela.",
      "Insatsen ska vara 1–2 % av sessionens bankroll.",
      "Vinst- och förluststopp skyddar både kapital och humör.",
      "Spelpaus och insättningsgränser är dina viktigaste verktyg.",
    ],
    takeaways_en: [
      "Set a monthly budget before you start.",
      "Bet size should be 1–2% of the session bankroll.",
      "Win and loss stops protect both capital and mood.",
      "Spelpaus and deposit limits are your most important tools.",
    ],
    faq: [
      {
        question_sv: "Hur ofta ska jag granska min spelbudget?",
        question_en: "How often should I review my gambling budget?",
        answer_sv:
          "Minst en gång i månaden. Justera om kostnader eller inkomst förändrats. Om du upptäcker att du regelbundet överskrider den — pausa.",
        answer_en:
          "At least monthly. Adjust if your costs or income change. If you find you regularly exceed it — take a break.",
      },
    ],
    updated_at: "2026-04-30",
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "guide-cs2",
    slug_sv: "cs2-betting",
    slug_en: "cs2-betting",
    category: "esports",
    title_sv: "CS2-betting — så satsar du på Counter-Strike 2",
    title_en: "CS2 betting — how to bet on Counter-Strike 2",
    excerpt_sv:
      "Spelmarknader, kartor och stora turneringar i CS2-betting förklarat.",
    excerpt_en:
      "Markets, maps and major tournaments in CS2 betting explained.",
    intro_sv:
      "Counter-Strike 2 är världens mest spelade taktiska shooter och en av de största esport-disciplinerna när det gäller bettingvolym. Spelet är en uppdaterad version av CS:GO och behåller den klassiska 5v5-strukturen där terrorister ska placera bomben och counter-terrorister ska stoppa dem. Att förstå turneringsmönster och kartmeta är avgörande om du vill satsa kvalificerat.",
    intro_en:
      "Counter-Strike 2 is the world's most-played tactical shooter and one of the largest esports disciplines by betting volume. The game is an upgraded version of CS:GO and keeps the classic 5v5 structure where terrorists plant the bomb and counter-terrorists try to stop them. Understanding tournament patterns and map meta is essential to betting intelligently.",
    sections: [
      {
        heading_sv: "Vanliga marknader",
        heading_en: "Common markets",
        paragraphs_sv: [
          "Match Winner är den enklaste marknaden — vem vinner matchen, oftast i bäst-av-3 eller bäst-av-5. Map Winner låter dig satsa på en specifik karta. Round Handicap (t.ex. -3,5 rundor) är populär hos värstingfavoriter. Total Maps Over/Under spelas oftast som Over 2,5 i BO3. Player Props finns för spelarmördanden (kills) och första kniv-frag.",
          "Live betting under matcher dominerar volymen. Odds rör sig snabbt när lag förlorar pistolrundan eller en nyckelspelare dör tidigt. Många operatörer erbjuder dessutom Round Winner-marknader där du satsar på en enskild rund.",
        ],
        paragraphs_en: [
          "Match Winner is the simplest market — who wins the match, typically best-of-3 or best-of-5. Map Winner lets you bet a specific map. Round Handicap (e.g. -3.5 rounds) is popular for heavy favourites. Total Maps Over/Under is most often Over 2.5 in BO3. Player props cover kills and first knife frag.",
          "Live betting during matches dominates the volume. Odds move fast when teams drop the pistol round or a key player dies early. Many operators also offer Round Winner markets where you bet on a single round.",
        ],
      },
      {
        heading_sv: "Kartmeta och förbud",
        heading_en: "Map meta and vetoes",
        paragraphs_sv: [
          "Före varje match väljer lagen vilka kartor som ska spelas via en veto-process. Lag är ofta starkare på vissa kartor — Inferno och Mirage är historiska standardkartor; Ancient och Anubis är nyare. Att kolla teamets win rate per karta hos sajter som HLTV är ett av de bästa sätten att hitta värde.",
        ],
        paragraphs_en: [
          "Before every match teams pick which maps will be played via a veto process. Teams are often stronger on certain maps — Inferno and Mirage are historic staples; Ancient and Anubis are newer. Checking each team's win rate per map on sites like HLTV is one of the best ways to find value.",
        ],
      },
      {
        heading_sv: "Stora turneringar",
        heading_en: "Major tournaments",
        paragraphs_sv: [
          "Counter-Strike-Major är säsongens absoluta höjdpunkt och hålls två gånger om året. ESL Pro League är en återkommande liga för topplagen. BLAST Premier är ett premier-circuit. IEM-evenemang som Katowice och Cologne lockar nästan lika stora odds som major-turneringarna.",
        ],
        paragraphs_en: [
          "The Counter-Strike Major is the season's headline event, held twice a year. ESL Pro League is a recurring league for top teams. BLAST Premier is a premier circuit. IEM events like Katowice and Cologne attract betting volume nearly equal to the Majors.",
        ],
      },
    ],
    takeaways_sv: [
      "Match Winner och Map Winner är de vanligaste marknaderna.",
      "Kartmeta är den största värdekällan — kontrollera HLTV.",
      "Live-odds rör sig snabbt vid pistolrunda och första kontakt.",
      "Major, ESL Pro League och IEM är de stora eventen.",
    ],
    takeaways_en: [
      "Match Winner and Map Winner are the most common markets.",
      "Map meta is the biggest source of value — check HLTV.",
      "Live odds swing fast on pistol rounds and first contact.",
      "Major, ESL Pro League and IEM are the headline events.",
    ],
    faq: [
      {
        question_sv: "Vilka spelbolag erbjuder CS2-betting i Sverige?",
        question_en: "Which sportsbooks offer CS2 betting in Sweden?",
        answer_sv:
          "Flera spelbolag med svensk licens erbjuder esport, bland annat de större bettingsajterna. Marknadsdjupet är dock större hos internationella sajter — väg risk mot utbud.",
        answer_en:
          "Several Swedish-licensed sportsbooks offer esports, including the larger names. Market depth is greater at international sites — weigh risk against selection.",
      },
    ],
    updated_at: "2026-04-30",
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "guide-dota",
    slug_sv: "dota2-betting",
    slug_en: "dota2-betting",
    category: "esports",
    title_sv: "Dota 2-betting — guide till TI och DPC",
    title_en: "Dota 2 betting — a guide to TI and the DPC",
    excerpt_sv:
      "Hur Dota 2-marknader fungerar, vad The International är och vilka lag som dominerar.",
    excerpt_en:
      "How Dota 2 markets work, what The International is, and which teams dominate.",
    intro_sv:
      "Dota 2 är en av världens mest tekniskt komplexa esport-titlar och samtidigt en av de mest lukrativa när det kommer till prispengar. Spelet är 5v5 där två lag försöker förstöra motståndarens Ancient. Patcher kommer regelbundet och kan vända meta upp och ner — vilket gör betting både spännande och svårt.",
    intro_en:
      "Dota 2 is one of the world's most technically complex esports titles and also one of the most lucrative in prize money. It is a 5v5 game where two teams try to destroy each other's Ancient. Patches arrive regularly and can flip the meta entirely — which makes betting both exciting and difficult.",
    sections: [
      {
        heading_sv: "Marknader att känna till",
        heading_en: "Markets to know",
        paragraphs_sv: [
          "Match Winner och Map Winner är vanligast. First Blood gäller vilket lag som tar första kill. First Tower och First Roshan är populära specialmarknader. Total Kills Over/Under är en av Dota-bettingens älsklingsmarknader eftersom kill-genomsnitten varierar enormt mellan patcher.",
        ],
        paragraphs_en: [
          "Match Winner and Map Winner are the most common. First Blood concerns the team that scores the first kill. First Tower and First Roshan are popular specials. Total Kills Over/Under is a Dota-betting favourite because the average changes dramatically with patches.",
        ],
      },
      {
        heading_sv: "DPC och The International",
        heading_en: "The DPC and The International",
        paragraphs_sv: [
          "Dota Pro Circuit (DPC) är den officiella ligastrukturen som leder fram till The International (TI), årets största turnering med en prispott som ofta överstiger 30 miljoner USD. Mellan TI byggs lagsammansättningar om — vilket gör säsongens första veckor svåra att satsa på.",
        ],
        paragraphs_en: [
          "The Dota Pro Circuit (DPC) is the official league structure leading to The International (TI), the year's biggest tournament with a prize pool that often exceeds USD 30 million. Between TIs rosters are rebuilt — which makes the first weeks of a season hard to bet.",
        ],
      },
      {
        heading_sv: "Patch-meta",
        heading_en: "Patch meta",
        paragraphs_sv: [
          "En ny Dota-patch kan flytta makten mellan position 1 (carry), 2 (mid), 3 (offlane), 4 (soft support) och 5 (hard support). En patch som stärker tidigt spel ger fler kills, mer aggression och högre kill-totals. En patch som stärker farm-spel sänker totals. Att läsa patch notes och titta på första veckan av högsta-rang offentliga matcher är en stor edge.",
        ],
        paragraphs_en: [
          "A new Dota patch can shift power between positions 1 (carry), 2 (mid), 3 (offlane), 4 (soft support) and 5 (hard support). A patch favouring early aggression yields more kills, more action and higher kill totals. A farming patch lowers totals. Reading patch notes and watching the first week of top-rank public matches is a big edge.",
        ],
      },
    ],
    takeaways_sv: [
      "TI har en av esportens största prispotter.",
      "First Blood, First Tower och Total Kills är populära marknader.",
      "Patcher förändrar meta — kontrollera datum före betting.",
      "Roster-byten mellan TI gör tidiga matcher osäkra.",
    ],
    takeaways_en: [
      "TI has one of the largest prize pools in esports.",
      "First Blood, First Tower and Total Kills are popular markets.",
      "Patches change the meta — check the patch date before betting.",
      "Roster swaps between TIs make early matches volatile.",
    ],
    faq: [
      {
        question_sv: "Är Dota 2-odds rörliga som CS2?",
        question_en: "Are Dota 2 odds as live-volatile as CS2?",
        answer_sv:
          "Live-odds rör sig snabbt vid First Blood, Roshan och stora teamfights. Reaktionen är ofta större än i CS2 eftersom Dota har fler spelmoment som direkt avgör matchens riktning.",
        answer_en:
          "Live odds move fast around First Blood, Roshan and major teamfights. Reactions are often bigger than in CS2 because Dota has more single moments that decide the match.",
      },
    ],
    updated_at: "2026-04-30",
  },

  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "guide-valorant",
    slug_sv: "valorant-betting",
    slug_en: "valorant-betting",
    category: "esports",
    title_sv: "Valorant-betting — agenter, kartor och VCT",
    title_en: "Valorant betting — agents, maps and the VCT",
    excerpt_sv:
      "Lär dig agenter, kartpool och format inför Valorant Champions Tour-betting.",
    excerpt_en:
      "Learn the agents, the map pool and the format ahead of Valorant Champions Tour betting.",
    intro_sv:
      "Valorant är Riot Games taktiska 5v5-shooter som kombinerar Counter-Strikes precision med agentförmågor i klass med MOBA-spel. Det officiella turneringssystemet heter Valorant Champions Tour (VCT) och består av regionala ligor och en Champions-final i slutet av säsongen.",
    intro_en:
      "Valorant is Riot Games' tactical 5v5 shooter combining Counter-Strike's precision with MOBA-grade agent abilities. The official competitive system is the Valorant Champions Tour (VCT), built around regional leagues and a Champions final at the end of the season.",
    sections: [
      {
        heading_sv: "Marknader och meta",
        heading_en: "Markets and meta",
        paragraphs_sv: [
          "Match Winner, Map Winner och Round Handicap dominerar. First To 13 är en specialmarknad som matchar Valorants format. Player props på agentbaserade ace-frags eller clutch-rundor börjar dyka upp hos vissa internationella sajter.",
          "Agent-meta påverkar hur lag spelar — Jett, Raze, Sage och Cypher har varit långvariga staples. När Riot patchar agenters förmågor påverkas ofta hela turneringens balans inom dagar.",
        ],
        paragraphs_en: [
          "Match Winner, Map Winner and Round Handicap dominate. First To 13 is a specialist market that matches Valorant's format. Player props on agent-specific ace-frags or clutches are appearing at some international sites.",
          "Agent meta drives team play — Jett, Raze, Sage and Cypher have been long-running staples. When Riot patches agent abilities, the entire tournament balance can shift within days.",
        ],
      },
      {
        heading_sv: "Kartpoolen",
        heading_en: "The map pool",
        paragraphs_sv: [
          "Riot roterar kartor in och ut ur poolen mellan splits. Bind, Haven, Split, Ascent, Icebox, Breeze, Fracture, Pearl och Lotus har alla cyklat genom poolen. Lag specialiserar sig snabbt på två-tre kartor — kontrollera deras win rate per karta hos vlr.gg innan du satsar map winner.",
        ],
        paragraphs_en: [
          "Riot rotates maps in and out of the pool between splits. Bind, Haven, Split, Ascent, Icebox, Breeze, Fracture, Pearl and Lotus have all cycled through. Teams specialise quickly on two or three maps — check their per-map win rate on vlr.gg before betting map winner.",
        ],
      },
    ],
    takeaways_sv: [
      "VCT är Riots officiella turneringssystem.",
      "Agent-patcher kan vända meta inom dagar.",
      "Match Winner, Map Winner och Round Handicap är vanligast.",
      "Kontrollera kartstatistik per lag på vlr.gg.",
    ],
    takeaways_en: [
      "The VCT is Riot's official competitive system.",
      "Agent patches can flip the meta within days.",
      "Match Winner, Map Winner and Round Handicap are most common.",
      "Check per-map statistics by team on vlr.gg.",
    ],
    faq: [
      {
        question_sv: "Skiljer sig Valorant-odds från CS2?",
        question_en: "How are Valorant odds different from CS2?",
        answer_sv:
          "Valorant har lägre kill-snitt per runda och mer agentbaserad varians, vilket gör round handicap volatilare än i CS2.",
        answer_en:
          "Valorant has fewer kills per round and more agent-driven variance, which makes round handicap more volatile than in CS2.",
      },
    ],
    updated_at: "2026-04-30",
  },
  {
    id: "guide-martingale",
    slug_sv: "martingale",
    slug_en: "martingale",
    category: "terms",
    title_sv: "Martingale — guide",
    title_en: "Martingale — English guide",
    excerpt_sv: "Allt om Martingale — information för svenska casinospelare.",
    excerpt_en: "Everything about Martingale — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Martingale i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Martingale in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-hur-vi-recenserar",
    slug_sv: "hur-vi-recenserar",
    slug_en: "hur-vi-recenserar",
    category: "terms",
    title_sv: "Hur Vi Recenserar — guide",
    title_en: "Hur Vi Recenserar — English guide",
    excerpt_sv: "Allt om Hur Vi Recenserar — information för svenska casinospelare.",
    excerpt_en: "Everything about Hur Vi Recenserar — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Hur Vi Recenserar i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Hur Vi Recenserar in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-andruccisystemet",
    slug_sv: "andruccisystemet",
    slug_en: "andruccisystemet",
    category: "terms",
    title_sv: "Andruccisystemet — guide",
    title_en: "Andruccisystemet — English guide",
    excerpt_sv: "Allt om Andruccisystemet — information för svenska casinospelare.",
    excerpt_en: "Everything about Andruccisystemet — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Andruccisystemet i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Andruccisystemet in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-strategier",
    slug_sv: "strategier",
    slug_en: "strategier",
    category: "terms",
    title_sv: "Strategier — guide",
    title_en: "Strategier — English guide",
    excerpt_sv: "Allt om Strategier — information för svenska casinospelare.",
    excerpt_en: "Everything about Strategier — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Strategier i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Strategier in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-kelly-kriteriet",
    slug_sv: "kelly-kriteriet",
    slug_en: "kelly-kriteriet",
    category: "terms",
    title_sv: "Kelly Kriteriet — guide",
    title_en: "Kelly Kriteriet — English guide",
    excerpt_sv: "Allt om Kelly Kriteriet — information för svenska casinospelare.",
    excerpt_en: "Everything about Kelly Kriteriet — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Kelly Kriteriet i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Kelly Kriteriet in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-parlay",
    slug_sv: "parlay",
    slug_en: "parlay",
    category: "terms",
    title_sv: "Parlay — guide",
    title_en: "Parlay — English guide",
    excerpt_sv: "Allt om Parlay — information för svenska casinospelare.",
    excerpt_en: "Everything about Parlay — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Parlay i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Parlay in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-reverse-labouchere",
    slug_sv: "reverse-labouchere",
    slug_en: "reverse-labouchere",
    category: "terms",
    title_sv: "Reverse Labouchere — guide",
    title_en: "Reverse Labouchere — English guide",
    excerpt_sv: "Allt om Reverse Labouchere — information för svenska casinospelare.",
    excerpt_en: "Everything about Reverse Labouchere — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Reverse Labouchere i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Reverse Labouchere in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-dalambert",
    slug_sv: "dalambert",
    slug_en: "dalambert",
    category: "terms",
    title_sv: "Dalambert — guide",
    title_en: "Dalambert — English guide",
    excerpt_sv: "Allt om Dalambert — information för svenska casinospelare.",
    excerpt_en: "Everything about Dalambert — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Dalambert i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Dalambert in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-fibonacci",
    slug_sv: "fibonacci",
    slug_en: "fibonacci",
    category: "terms",
    title_sv: "Fibonacci — guide",
    title_en: "Fibonacci — English guide",
    excerpt_sv: "Allt om Fibonacci — information för svenska casinospelare.",
    excerpt_en: "Everything about Fibonacci — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Fibonacci i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Fibonacci in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-labouchere",
    slug_sv: "labouchere",
    slug_en: "labouchere",
    category: "terms",
    title_sv: "Labouchere — guide",
    title_en: "Labouchere — English guide",
    excerpt_sv: "Allt om Labouchere — information för svenska casinospelare.",
    excerpt_en: "Everything about Labouchere — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Labouchere i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Labouchere in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-parolisystemet",
    slug_sv: "parolisystemet",
    slug_en: "parolisystemet",
    category: "terms",
    title_sv: "Parolisystemet — guide",
    title_en: "Parolisystemet — English guide",
    excerpt_sv: "Allt om Parolisystemet — information för svenska casinospelare.",
    excerpt_en: "Everything about Parolisystemet — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Parolisystemet i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Parolisystemet in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-husets-fordel",
    slug_sv: "husets-fordel",
    slug_en: "husets-fordel",
    category: "terms",
    title_sv: "Husets Fordel — guide",
    title_en: "Husets Fordel — English guide",
    excerpt_sv: "Allt om Husets Fordel — information för svenska casinospelare.",
    excerpt_en: "Everything about Husets Fordel — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Husets Fordel i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Husets Fordel in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-rng",
    slug_sv: "rng",
    slug_en: "rng",
    category: "terms",
    title_sv: "Rng — guide",
    title_en: "Rng — English guide",
    excerpt_sv: "Allt om Rng — information för svenska casinospelare.",
    excerpt_en: "Everything about Rng — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Rng i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Rng in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-vad-rtp-ar",
    slug_sv: "vad-rtp-ar",
    slug_en: "vad-rtp-ar",
    category: "terms",
    title_sv: "Vad Rtp Ar — guide",
    title_en: "Vad Rtp Ar — English guide",
    excerpt_sv: "Allt om Vad Rtp Ar — information för svenska casinospelare.",
    excerpt_en: "Everything about Vad Rtp Ar — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Vad Rtp Ar i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Vad Rtp Ar in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-vilket-casino-pa-natet-som-ar-bast",
    slug_sv: "vilket-casino-pa-natet-som-ar-bast",
    slug_en: "vilket-casino-pa-natet-som-ar-bast",
    category: "terms",
    title_sv: "Vilket Casino Pa Natet Som Ar Bast — guide",
    title_en: "Vilket Casino Pa Natet Som Ar Bast — English guide",
    excerpt_sv: "Allt om Vilket Casino Pa Natet Som Ar Bast — information för svenska casinospelare.",
    excerpt_en: "Everything about Vilket Casino Pa Natet Som Ar Bast — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Vilket Casino Pa Natet Som Ar Bast i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Vilket Casino Pa Natet Som Ar Bast in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-casino-ordlista",
    slug_sv: "casino-ordlista",
    slug_en: "casino-ordlista",
    category: "terms",
    title_sv: "Casino Ordlista — guide",
    title_en: "Casino Ordlista — English guide",
    excerpt_sv: "Allt om Casino Ordlista — information för svenska casinospelare.",
    excerpt_en: "Everything about Casino Ordlista — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Casino Ordlista i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Casino Ordlista in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-sakerhet",
    slug_sv: "sakerhet",
    slug_en: "sakerhet",
    category: "terms",
    title_sv: "Sakerhet — guide",
    title_en: "Sakerhet — English guide",
    excerpt_sv: "Allt om Sakerhet — information för svenska casinospelare.",
    excerpt_en: "Everything about Sakerhet — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Sakerhet i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Sakerhet in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-bonusrundor",
    slug_sv: "bonusrundor",
    slug_en: "bonusrundor",
    category: "terms",
    title_sv: "Bonusrundor — guide",
    title_en: "Bonusrundor — English guide",
    excerpt_sv: "Allt om Bonusrundor — information för svenska casinospelare.",
    excerpt_en: "Everything about Bonusrundor — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Bonusrundor i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Bonusrundor in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-insattningsguide",
    slug_sv: "insattningsguide",
    slug_en: "insattningsguide",
    category: "terms",
    title_sv: "Insattningsguide — guide",
    title_en: "Insattningsguide — English guide",
    excerpt_sv: "Allt om Insattningsguide — information för svenska casinospelare.",
    excerpt_en: "Everything about Insattningsguide — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Insattningsguide i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Insattningsguide in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-regler-och-villkor",
    slug_sv: "regler-och-villkor",
    slug_en: "regler-och-villkor",
    category: "terms",
    title_sv: "Regler Och Villkor — guide",
    title_en: "Regler Och Villkor — English guide",
    excerpt_sv: "Allt om Regler Och Villkor — information för svenska casinospelare.",
    excerpt_en: "Everything about Regler Och Villkor — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Regler Och Villkor i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Regler Och Villkor in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-hur-vet-man-svensk-licens",
    slug_sv: "hur-vet-man-svensk-licens",
    slug_en: "hur-vet-man-svensk-licens",
    category: "terms",
    title_sv: "Hur Vet Man Svensk Licens — guide",
    title_en: "Hur Vet Man Svensk Licens — English guide",
    excerpt_sv: "Allt om Hur Vet Man Svensk Licens — information för svenska casinospelare.",
    excerpt_en: "Everything about Hur Vet Man Svensk Licens — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Hur Vet Man Svensk Licens i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Hur Vet Man Svensk Licens in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-omsattningskrav-guide",
    slug_sv: "omsattningskrav-guide",
    slug_en: "omsattningskrav-guide",
    category: "terms",
    title_sv: "Omsattningskrav Guide — guide",
    title_en: "Omsattningskrav Guide — English guide",
    excerpt_sv: "Allt om Omsattningskrav Guide — information för svenska casinospelare.",
    excerpt_en: "Everything about Omsattningskrav Guide — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Omsattningskrav Guide i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Omsattningskrav Guide in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-vanliga-problem",
    slug_sv: "vanliga-problem",
    slug_en: "vanliga-problem",
    category: "terms",
    title_sv: "Vanliga Problem — guide",
    title_en: "Vanliga Problem — English guide",
    excerpt_sv: "Allt om Vanliga Problem — information för svenska casinospelare.",
    excerpt_en: "Everything about Vanliga Problem — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Vanliga Problem i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Vanliga Problem in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-jaga-forluster",
    slug_sv: "jaga-forluster",
    slug_en: "jaga-forluster",
    category: "terms",
    title_sv: "Jaga Forluster — guide",
    title_en: "Jaga Forluster — English guide",
    excerpt_sv: "Allt om Jaga Forluster — information för svenska casinospelare.",
    excerpt_en: "Everything about Jaga Forluster — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Jaga Forluster i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Jaga Forluster in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
  {
    id: "guide-behandla-spelproblem",
    slug_sv: "behandla-spelproblem",
    slug_en: "behandla-spelproblem",
    category: "terms",
    title_sv: "Behandla Spelproblem — guide",
    title_en: "Behandla Spelproblem — English guide",
    excerpt_sv: "Allt om Behandla Spelproblem — information för svenska casinospelare.",
    excerpt_en: "Everything about Behandla Spelproblem — information for Swedish casino players.",
    intro_sv:
      "Den här guiden ger en introduktion till Behandla Spelproblem i kontexten av svenskt casinospel.",
    intro_en:
      "This guide provides an introduction to Behandla Spelproblem in the context of Swedish casino gambling.",
    sections: [
      {
        heading_sv: "Översikt",
        heading_en: "Overview",
        paragraphs_sv: ["Läs villkoren och sätt en spelbudget innan du spelar."],
        paragraphs_en: ["Read the terms and set a gambling budget before playing."],
      },
    ],
    takeaways_sv: ["Spela inom din budget."],
    takeaways_en: ["Play within your budget."],
    faq: [],
    updated_at: "2026-05-01",
  },
];

export function findGuide(slug: string): Guide | undefined {
  return guides.find((g) => g.slug_sv === slug || g.slug_en === slug);
}
